import { useMemo, useState } from "react";
import styled from "styled-components";
import { fonts } from "../../../styles/theme";
import { Barra, Card, Chip, Chips, Etiqueta, Meta, Numero, Pilula, cores } from "../../relatorio/styles";
import { NumeroAnimado } from "../../relatorio/componentes/numero-animado";
import {
  FILTROS_DE_CONTA,
  descricaoDoConvite,
  explicarErro,
  filtrarContas,
  iniciais,
  resumoDasContas,
  resumoDosPerfis,
  situacaoDaConta,
  CORES_KPI,
  type FiltroDeConta,
} from "../pages/contas";
import { desdeQuando } from "../pages/estados";
import { LinkDeAcesso } from "./link-de-acesso";
import { PEDE_ACESSO, usePedidosDeAcesso, type GerarLink } from "../pages/acesso";
import { descricaoDoCliente, situacaoDoCliente } from "../pages/cliente";
import type { ContaConectada, EstadoDeAcesso, PerfilQualidade } from "../types";

const Secao = styled.section`
  font-family: ${fonts.gilroyRegular}, system-ui, sans-serif;
  color: ${cores.ink};
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 36px;
  min-width: 0;

  h1 {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 26px;
    letter-spacing: -0.02em;
    margin: 8px 0 0;
  }
`;

const Kpis = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  @media (min-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }
`;

const Kpi = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 20px;
`;

const Rotulo = styled(Meta)`
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  color: ${cores.ink3};
  font-size: 13px;
`;

const Ferramentas = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;

  input {
    flex: 1;
    min-width: 220px;
    border: 1px solid ${cores.line};
    border-radius: 1000px;
    padding: 11px 18px;
    font: inherit;
    font-size: 14px;
    outline: none;
    background: #fff;
  }
`;

const Linha = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 14px;
  padding: 16px 0;
  border-top: 1px solid ${cores.line};
  &:first-child {
    border-top: 0;
  }
  @media (min-width: 1000px) {
    grid-template-columns: 44px 1.4fr 1fr auto;
    align-items: center;
  }
`;

const Avatar = styled.div<{ $cor: string; $fundo: string }>`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: ${({ $fundo }) => $fundo};
  color: ${({ $cor }) => $cor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 14px;
`;

const Botaozinho = styled.button<{ $cor?: string }>`
  border: 1px solid ${cores.line};
  background: #fff;
  color: ${({ $cor }) => $cor ?? cores.blue};
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 13px;
  border-radius: 1000px;
  padding: 8px 14px;
  cursor: pointer;
  white-space: nowrap;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Detalhe = styled.div`
  padding: 0 0 16px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (min-width: 1000px) {
    padding-left: 58px;
  }
`;

const ItemDePerfil = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid ${cores.line};
  font-size: 14px;
  &:first-child {
    border-top: 0;
  }
  a {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
  }
`;

const SITUACOES: { chave: EstadoDeAcesso; rotulo: string; cor: keyof typeof CORES_KPI; contagem: (c: ContaConectada) => number }[] = [
  { chave: "SEM_ACESSO", rotulo: "sem acesso", cor: "vermelha", contagem: (c) => c.semAcesso },
  { chave: "SUSPENSO", rotulo: "suspensos", cor: "amarela", contagem: (c) => c.suspensos },
  { chave: "COPIA", rotulo: "cópias", cor: "roxa", contagem: (c) => c.copias },
  { chave: "ACESSO_SOLICITADO", rotulo: "acesso solicitado", cor: "azul", contagem: (c) => c.acessoSolicitado },
];

type Props = {
  contas: ContaConectada[];
  carregando: boolean;
  verificando: number | "todas" | null;
  onVerificar: (userId: number) => void;
  onVerificarTodas: () => void;
  onListarPerfis: (email: string, estado: EstadoDeAcesso) => Promise<PerfilQualidade[]>;
  onPedirAcesso: (publicId: string) => Promise<boolean>;
  onGerarLink: GerarLink;
};

export const ContasConectadas = ({ contas, carregando, verificando, onVerificar, onVerificarTodas, onListarPerfis, onPedirAcesso, onGerarLink }: Props) => {
  const { pedidoDe, gerar, gerando } = usePedidosDeAcesso(onGerarLink);
  const [filtro, setFiltro] = useState<FiltroDeConta>("TODAS");
  const [busca, setBusca] = useState("");
  const [convitesAbertos, setConvitesAbertos] = useState<number | null>(null);
  const [perfisAbertos, setPerfisAbertos] = useState<{ userId: number; estado: EstadoDeAcesso; lista: PerfilQualidade[]; carregando: boolean } | null>(null);
  const resumo = useMemo(() => resumoDasContas(contas), [contas]);
  const visiveis = useMemo(() => filtrarContas(contas, filtro, busca), [contas, filtro, busca]);
  const ultimaChecagem = useMemo(
    () => contas.map((c) => c.invitationsCheckedAt).filter(Boolean).sort().at(-1) ?? null,
    [contas],
  );

  const abrirPerfis = async (conta: ContaConectada, estado: EstadoDeAcesso) => {
    if (perfisAbertos?.userId === conta.userId && perfisAbertos.estado === estado) {
      setPerfisAbertos(null);
      return;
    }
    setPerfisAbertos({ userId: conta.userId, estado, lista: [], carregando: true });
    const lista = await onListarPerfis(conta.email, estado);
    setPerfisAbertos((atual) => (atual?.userId === conta.userId && atual.estado === estado ? { ...atual, lista, carregando: false } : atual));
  };

  const pedirAcesso = async (publicId: string) => {
    if (await onPedirAcesso(publicId)) {
      setPerfisAbertos((atual) => (atual ? { ...atual, lista: atual.lista.filter((perfil) => perfil.publicId !== publicId) } : atual));
    }
  };

  return (
    <Secao aria-label="Contas conectadas">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <h1>Contas conectadas ao Google</h1>
          <Meta>
            Cada e-mail que administra perfis: se a conexão está de pé, quais perfis dependem dele e se há convites esperando aceite.
            {ultimaChecagem ? ` Convites conferidos ${desdeQuando(ultimaChecagem)}.` : " Convites ainda não conferidos."}
          </Meta>
        </div>
        <Pilula
          as="button"
          type="button"
          $tipo="contorno"
          onClick={onVerificarTodas}
          disabled={verificando !== null}
          style={{ padding: "10px 20px", fontSize: 14 }}
        >
          {verificando === "todas" ? "Verificando…" : "Verificar convites em todas"}
        </Pilula>
      </div>

      <Kpis>
        <Kpi $fundo={CORES_KPI.verde.fundo}>
          <Rotulo>Contas conectadas</Rotulo>
          <Numero $tamanho={40} $cor={CORES_KPI.verde.cor}>
            <NumeroAnimado valor={resumo.conectadas} />
          </Numero>
          <Meta>{`de ${contas.length - resumo.semToken} contas que já conectaram`}</Meta>
        </Kpi>
        <Kpi $fundo={CORES_KPI.vermelha.fundo}>
          <Rotulo>Precisam reconectar</Rotulo>
          <Numero $tamanho={40} $cor={CORES_KPI.vermelha.cor}>
            <NumeroAnimado valor={resumo.reconectar} />
          </Numero>
          <Meta>{resumo.semToken > 0 ? `token recusado pelo Google · ${resumo.semToken} nunca conectaram` : "token recusado pelo Google"}</Meta>
        </Kpi>
        <Kpi $fundo={CORES_KPI.amarela.fundo}>
          <Rotulo>Convites pendentes</Rotulo>
          <Numero $tamanho={40} $cor={CORES_KPI.amarela.cor}>
            <NumeroAnimado valor={resumo.convites} />
          </Numero>
          <Meta>alguém nos convidou para gerenciar uma ficha</Meta>
        </Kpi>
        <Kpi $fundo={CORES_KPI.azul.fundo}>
          <Rotulo>Perfis administrados</Rotulo>
          <Numero $tamanho={40} $cor={CORES_KPI.azul.cor}>
            <NumeroAnimado valor={resumo.perfis} />
          </Numero>
          <Meta>{`${resumo.comProblema.toLocaleString("pt-BR")} com problema de acesso`}</Meta>
        </Kpi>
      </Kpis>

      <Ferramentas>
        <input
          type="search"
          placeholder="Buscar conta por e-mail ou nome"
          aria-label="Buscar conta"
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
        />
        <Chips role="tablist" aria-label="Filtro de contas">
          {FILTROS_DE_CONTA.map((item) => (
            <Chip key={item.chave} type="button" role="tab" aria-selected={filtro === item.chave} $ativo={filtro === item.chave} onClick={() => setFiltro(item.chave)}>
              {item.rotulo}
            </Chip>
          ))}
        </Chips>
      </Ferramentas>

      <Card style={{ padding: "6px 22px", minWidth: 0 }}>
        {carregando && <Meta style={{ display: "block", padding: "14px 0" }}>Carregando contas…</Meta>}
        {!carregando && visiveis.length === 0 && <Meta style={{ display: "block", padding: "14px 0" }}>Nenhuma conta nesta situação.</Meta>}
        {visiveis.map((conta) => {
          const situacao = situacaoDaConta(conta);
          const tema = CORES_KPI[situacao.cor];
          const perfis = resumoDosPerfis(conta);
          const erro = explicarErro(conta.invitationsError);
          const convitesVisiveis = convitesAbertos === conta.userId;
          const situacoesComPerfis = SITUACOES.filter((s) => s.contagem(conta) > 0);
          const detalhe = perfisAbertos?.userId === conta.userId ? perfisAbertos : null;
          return (
            <div key={conta.userId}>
              <Linha>
                <Avatar $cor={tema.cor} $fundo={tema.fundo}>{iniciais(conta.name, conta.email)}</Avatar>
                <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15, overflowWrap: "anywhere" }}>{conta.email}</span>
                    <Etiqueta $cor={situacao.cor}>{situacao.rotulo}</Etiqueta>
                    {conta.invitationsPending > 0 && (
                      <Etiqueta $cor="amarela">{`${conta.invitationsPending} ${conta.invitationsPending === 1 ? "convite pendente" : "convites pendentes"}`}</Etiqueta>
                    )}
                  </div>
                  <Meta>
                    {conta.name && conta.name !== conta.email ? `${conta.name} · ` : ""}
                    {conta.tokenSince ? `conectada ${desdeQuando(conta.tokenSince)}` : "nunca conectada"}
                  </Meta>
                  {erro && (
                    <Meta style={{ color: CORES_KPI.vermelha.cor, fontFamily: fonts.gilroySemiBold }}>{erro}</Meta>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <Meta>{`${conta.profiles.toLocaleString("pt-BR")} ${conta.profiles === 1 ? "perfil" : "perfis"}`}</Meta>
                    <Meta style={{ color: perfis.alerta ? CORES_KPI.vermelha.cor : cores.ink4, fontFamily: fonts.gilroySemiBold }}>{perfis.texto}</Meta>
                  </div>
                  <Barra $largura={conta.profiles ? (conta.comAcesso / conta.profiles) * 100 : 0} $cor={CORES_KPI.verde.cor} $atraso={0.2} />
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {situacoesComPerfis.length === 0 && <Meta>{conta.profiles > 0 ? `${conta.comAcesso} com acesso` : "—"}</Meta>}
                    {situacoesComPerfis.map((s) => (
                      <Botaozinho
                        key={s.chave}
                        type="button"
                        $cor={CORES_KPI[s.cor].cor}
                        aria-pressed={detalhe?.estado === s.chave}
                        onClick={() => abrirPerfis(conta, s.chave)}
                        style={{ padding: "4px 10px", fontSize: 12, background: detalhe?.estado === s.chave ? CORES_KPI[s.cor].fundo : "#fff" }}
                        title={`Ver os perfis ${s.rotulo} desta conta`}
                      >
                        {`${s.contagem(conta)} ${s.rotulo} ›`}
                      </Botaozinho>
                    ))}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {conta.invitationsPending > 0 && (
                    <Botaozinho type="button" onClick={() => setConvitesAbertos(convitesVisiveis ? null : conta.userId)}>
                      {convitesVisiveis ? "Ocultar convites" : "Ver convites"}
                    </Botaozinho>
                  )}
                  <Botaozinho type="button" onClick={() => onVerificar(conta.userId)} disabled={verificando !== null || !conta.hasToken}>
                    {verificando === conta.userId ? "Consultando…" : "Verificar convites"}
                  </Botaozinho>
                </div>
              </Linha>
              {convitesVisiveis && conta.invitations.length > 0 && (
                <Detalhe>
                  {conta.invitations.map((convite) => (
                    <Card key={convite.invitationName} $fundo={CORES_KPI.amarela.fundo} $compacto style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 14 }}>{descricaoDoConvite(convite)}</span>
                      <Meta>
                        {[convite.targetAddress, convite.accountTitle ? `conta ${convite.accountTitle}` : null, convite.checkedAt ? `visto ${desdeQuando(convite.checkedAt)}` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </Meta>
                      <Meta>Para aceitar, entre no Perfil da Empresa com este e-mail e aceite o convite.</Meta>
                    </Card>
                  ))}
                </Detalhe>
              )}
              {detalhe && (
                <Detalhe>
                  <Card $fundo={cores.grayTint} style={{ padding: "6px 18px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, padding: "10px 0 4px" }}>
                      <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 14 }}>
                        {`Perfis ${SITUACOES.find((s) => s.chave === detalhe.estado)?.rotulo} · ${conta.email}`}
                      </span>
                      <Meta>{detalhe.carregando ? "carregando…" : `${detalhe.lista.length} ${detalhe.lista.length === 1 ? "perfil" : "perfis"}`}</Meta>
                    </div>
                    {!detalhe.carregando && detalhe.lista.length === 0 && <Meta style={{ display: "block", padding: "8px 0" }}>Nenhum perfil nesta situação.</Meta>}
                    {detalhe.lista.map((perfil) => (
                      <ItemDePerfil key={perfil.publicId}>
                        <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                          <span>
                            <a href={`/dashboard/${perfil.publicId}`} target="_blank" rel="noopener noreferrer">{perfil.name}</a>
                            <Meta>{perfil.city ? ` — ${perfil.city}/${perfil.state ?? ""}` : ""}{` · conferido ${desdeQuando(perfil.accessCheckedAt)}`}</Meta>
                          </span>
                          {(descricaoDoCliente(perfil) || situacaoDoCliente(perfil.customerStatus)) && (
                            <Meta>
                              {descricaoDoCliente(perfil) ? `Cliente: ${descricaoDoCliente(perfil)}` : ""}
                              {situacaoDoCliente(perfil.customerStatus) ? ` · ${situacaoDoCliente(perfil.customerStatus)}` : ""}
                            </Meta>
                          )}
                        </span>
                        {PEDE_ACESSO.includes(detalhe.estado) && (
                          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                            <LinkDeAcesso pedido={pedidoDe(perfil)} gerando={gerando === perfil.publicId} onGerar={() => gerar(perfil.publicId)} />
                            {detalhe.estado === "SEM_ACESSO" && (
                              <Botaozinho type="button" $cor={cores.ink3} onClick={() => pedirAcesso(perfil.publicId)}>Marcar acesso solicitado</Botaozinho>
                            )}
                          </span>
                        )}
                      </ItemDePerfil>
                    ))}
                  </Card>
                </Detalhe>
              )}
            </div>
          );
        })}
      </Card>

      <Meta>
        O que o Google mostra por API: conexão da conta, situação de cada perfil e convites recebidos. Pedidos de propriedade feitos por terceiros e os avisos por e-mail não são expostos pela API — chegam só na caixa de entrada de cada conta.
      </Meta>
    </Secao>
  );
};
